/**
 * Sentry PHI scrubbing for the legacy webpack bundle.
 *
 * This is the JS counterpart to frontend/src/shared/sentryScrub.ts (the Vite
 * apps) — the two can't share a module across the webpack/Vite boundary, so the
 * legacy bundle carries its own copy. Its job is different from the Vite one:
 * the cloud/legacy surface has no patient access tokens in URLs, but the exam,
 * user and scanner tables put filter/search values into the *query string* of
 * the AJAX calls they make (e.g. `/api/exams/?patient_name=John&patient_msp=123`).
 * Sentry's fetch/xhr breadcrumbs capture those URLs by default, and any error or
 * transaction event then ships them to Sentry — so on a PHI/BAA app those query
 * values must be redacted before an event leaves the browser.
 *
 * We redact the *value* of every query parameter except a small allowlist of
 * operational params. This is fail-safe: a patient filter added later is scrubbed
 * automatically, and we never have to enumerate which keys are sensitive. We
 * deliberately do NOT touch path segments — legacy cloud paths contain long
 * snake_case route words and resource UUIDs that are not PHI and are useful for
 * debugging.
 */

// Query params whose values are safe to keep in Sentry breadcrumb/event URLs.
// Matched case-insensitively; everything else has its value redacted.
const SAFE_QUERY_PARAMS = new Set(["page", "page_size", "page_num", "cursor", "offset", "limit", "ordering", "format"]);

/** Redact the value of every non-allowlisted query param in a URL/path string. */
export function redactUrl(value) {
    if (typeof value !== "string" || value === "") {
        return value;
    }
    try {
        const url = new URL(value, window.location.origin);
        let changed = false;
        for (const key of [...url.searchParams.keys()]) {
            if (!SAFE_QUERY_PARAMS.has(key.toLowerCase())) {
                url.searchParams.set(key, "[Filtered]");
                changed = true;
            }
        }
        if (!changed) {
            return value;
        }
        // Preserve whether the original was absolute or a bare path.
        return /^[a-z][a-z0-9+.-]*:\/\//i.test(value) ? url.toString() : url.pathname + url.search + url.hash;
    } catch (_e) {
        // Not a parseable URL — nothing query-shaped to redact.
        return value;
    }
}

/**
 * Scrub query values out of a breadcrumb's URL fields. Mutates and returns the
 * breadcrumb so it can be used directly as Sentry's `beforeBreadcrumb` hook.
 */
export function scrubBreadcrumb(breadcrumb) {
    if (breadcrumb && typeof breadcrumb.message === "string") {
        breadcrumb.message = redactUrl(breadcrumb.message);
    }
    const data = breadcrumb?.data;
    if (data) {
        // navigation breadcrumbs use `to`/`from`; fetch & xhr use `url`.
        for (const key of ["url", "to", "from"]) {
            if (typeof data[key] === "string") {
                data[key] = redactUrl(data[key]);
            }
        }
    }
    return breadcrumb;
}

/**
 * Scrub an event's `request.url` and any embedded breadcrumbs. Works for both
 * error events (`beforeSend`) and transactions (`beforeSendTransaction`).
 */
export function scrubEvent(event) {
    if (event?.request && typeof event.request.url === "string") {
        event.request.url = redactUrl(event.request.url);
    }
    if (event && Array.isArray(event.breadcrumbs)) {
        for (const breadcrumb of event.breadcrumbs) {
            scrubBreadcrumb(breadcrumb);
        }
    }
    return event;
}
